
import { useQuery } from "@tanstack/react-query";
import { EventCard } from "./EventCard";
import { supabase } from "@/integrations/supabase/client";
import { Event } from "@/lib/types";
import { Skeleton } from "@/components/ui/skeleton";
import { useCallback, memo } from "react";
import { toast } from "sonner";

export const FeaturedEvents = memo(() => {
  const fetchEvents = useCallback(async () => {
    try {
      console.log("Fetching featured events...");
      
      // Calculate the cutoff time (current time minus 4 hours)
      const cutoffTime = new Date();
      cutoffTime.setHours(cutoffTime.getHours() - 4);
      const cutoffTimeISO = cutoffTime.toISOString();
      
      const { data, error } = await supabase
        .from("events")
        .select("*")
        .eq("status", "published") // Only fetch published events
        .gte("date", cutoffTimeISO) // Only fetch events that haven't passed by more than 2 hours
        .order("date", { ascending: true })
        .limit(6);

      if (error) {
        console.error("Error fetching events:", error);
        throw error;
      }

      console.log("Fetched events:", data);
      return data as Event[];
    } catch (error) {
      console.error("Failed to fetch events:", error);
      toast.error("Failed to load events. Please try again later.");
      throw error;
    }
  }, []);

  const { data: events, isLoading, error } = useQuery({
    queryKey: ["featured-events"],
    queryFn: fetchEvents,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 3,
  });

  if (error) {
    console.error("Error in FeaturedEvents:", error);
    return (
      <div className="text-center text-red-500 p-4 rounded-lg bg-red-500/10">
        Unable to load events. Please try again later.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
      {isLoading
        ? Array(6)
            .fill(null)
            .map((_, i) => (
              <div key={i} className="space-y-3">
                <Skeleton className="h-[200px] w-full bg-gray-800" />
                <Skeleton className="h-4 w-3/4 bg-gray-800" />
                <Skeleton className="h-4 w-1/2 bg-gray-800" />
              </div>
            ))
        : events?.map((event) => (
            <EventCard key={event.id} event={event} />
          ))}
    </div>
  );
});

FeaturedEvents.displayName = "FeaturedEvents";
